// Deterministic random helpers. Every level, daily seed and challenge link
// depends on these being bit-for-bit stable across devices and releases, so
// nothing here may ever use Math.random or platform-specific behaviour.

/** FNV-1a 32-bit hash of a string → uint32. */
export function hashString(str) {
  const s = String(str);
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** mulberry32 PRNG. Returns a function producing numbers in [0, 1). */
export function mulberry32(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Normalise any seed (number or string) to a uint32. */
export function normaliseSeed(seed) {
  if (typeof seed === 'number' && Number.isFinite(seed)) return seed >>> 0;
  return hashString(seed == null ? '' : seed);
}

/**
 * Seeded RNG with the handful of helpers the generator needs.
 * `shuffle` returns a new array and never mutates its input.
 */
export function makeRng(seed) {
  const next = mulberry32(normaliseSeed(seed));
  const rng = {
    next,
    int(lo, hi) {
      const a = Math.ceil(Math.min(lo, hi));
      const b = Math.floor(Math.max(lo, hi));
      return a + Math.floor(next() * (b - a + 1));
    },
    pick(arr) {
      if (!arr || arr.length === 0) return undefined;
      return arr[Math.floor(next() * arr.length)];
    },
    chance(p) {
      return next() < p;
    },
    shuffle(arr) {
      const out = Array.from(arr);
      for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(next() * (i + 1));
        const tmp = out[i];
        out[i] = out[j];
        out[j] = tmp;
      }
      return out;
    },
  };
  return rng;
}
